import { observable, action, computed, runInAction } from 'mobx'
import { notification as notifApi } from '../api/hokan'
import { NotificationHistory, NotificationPriority } from '../types/notification'
import { errorMessage, mapPaginationMeta } from '../components/notification/shared'

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
}

export class NotificationStore {
  @observable public unreadCount: number = 0
  @observable public histories: NotificationHistory[] = []
  @observable public urgentHistories: NotificationHistory[] = []
  @observable public pagination: Pagination = {
    current_page: 1,
    total_pages: 1,
    total_count: 0,
  }
  @observable public panelOpen: boolean = false
  @observable public selectedHistoryId: number | null = null
  @observable public loading: boolean = false
  @observable public error: string | null = null
  @observable public nowTick: number = Date.now()
  @observable public currentUserId: number | null = null
  @observable public unreadOnly: boolean = false
  @observable public searchQuery: string = ''
  @observable public priorityFilter: NotificationPriority | '' = ''

  @action
  public setUnreadOnly(v: boolean) {
    this.unreadOnly = v
  }

  @action
  public setSearchQuery(q: string) {
    this.searchQuery = q
  }

  @action
  public setPriorityFilter(p: NotificationPriority | '') {
    this.priorityFilter = p
  }

  @action
  public setCurrentUserId(id: number | null) {
    this.currentUserId = id
  }

  @action
  public setUnreadCount(n: number) {
    this.unreadCount = n
  }

  @action
  public openPanel(historyId?: number | null) {
    this.panelOpen = true
    if (historyId !== undefined) {
      this.selectedHistoryId = historyId
    }
  }

  @action
  public closePanel() {
    this.panelOpen = false
  }

  @action
  public selectHistory(historyId: number | null) {
    this.selectedHistoryId = historyId
  }

  @action
  public async refreshUnreadCount(): Promise<boolean> {
    runInAction(() => {
      this.nowTick = Date.now()
    })
    try {
      const res = await notifApi.unreadCount()
      const prev = this.unreadCount
      runInAction(() => {
        this.unreadCount = res.data.count
      })
      if (res.data.count !== prev) {
        // Always refresh the urgent banner source. The panel feed is only visible
        // when open (and re-fetches itself on open), so skip it while closed.
        const tasks = [this.fetchUrgent()]
        if (this.panelOpen) tasks.push(this.fetchFeed(1))
        await Promise.all(tasks)
      }
      return true
    } catch (e) {
      return false
    }
  }

  @action
  public async fetchFeed(page: number = 1) {
    this.loading = true
    this.error = null
    try {
      const params: {
        page: number
        per_page: number
        unread_only?: boolean
        q?: string
        priority?: NotificationPriority
      } = {
        page,
        per_page: 20,
      }
      if (this.unreadOnly) params.unread_only = true
      const trimmed = this.searchQuery.trim()
      if (trimmed) params.q = trimmed
      if (this.priorityFilter) params.priority = this.priorityFilter
      const res = await notifApi.feed(params)
      runInAction(() => {
        if (page === 1) {
          // Preserve optimistic seen_at across a refetch so a just-read item
          // doesn't flash back to unread before the server reflects the change.
          const localSeen = new Map<number, string>()
          this.histories.forEach(h => {
            if (h.seen_at) localSeen.set(h.id, h.seen_at)
          })
          this.histories = res.data.data.map(h => {
            const seen = localSeen.get(h.id)
            return seen && !h.seen_at ? Object.assign({}, h, { seen_at: seen }) : h
          })
        } else {
          this.histories = this.histories.concat(res.data.data)
        }
        this.pagination = mapPaginationMeta(res.data.pagination)
        this.loading = false
      })
    } catch (e) {
      runInAction(() => {
        this.error = errorMessage(e, 'Failed to load feed')
        this.loading = false
      })
    }
  }

  @action
  public async fetchUrgent() {
    try {
      const res = await notifApi.feed({
        page: 1,
        per_page: 20,
        unread_only: true,
        priority: 'urgent',
      })
      runInAction(() => {
        const localSeen = new Map<number, string>()
        this.urgentHistories.forEach(h => {
          if (h.seen_at) localSeen.set(h.id, h.seen_at)
        })
        this.urgentHistories = res.data.data.map(h => {
          const seen = localSeen.get(h.id)
          return seen && !h.seen_at ? Object.assign({}, h, { seen_at: seen }) : h
        })
      })
    } catch (e) {
      // swallow
    }
  }

  // An item can live in both the feed and the urgent banner, so seen state +
  // unread count are updated optimistically in both lists (with rollback on failure).
  @action
  public async markSeen(historyId: number) {
    const idx = this.histories.findIndex(h => h.id === historyId)
    const prev = idx >= 0 ? this.histories[idx] : null
    const wasUnread = prev && !prev.seen_at

    const uIdx = this.urgentHistories.findIndex(h => h.id === historyId)
    const uPrev = uIdx >= 0 ? this.urgentHistories[uIdx] : null
    const uWasUnread = uPrev && !uPrev.seen_at

    if (wasUnread && prev) {
      runInAction(() => {
        const updated = Object.assign({}, prev, { seen_at: new Date().toISOString() })
        const next = this.histories.slice()
        next[idx] = updated
        this.histories = next
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      })
    }
    if (uWasUnread && uPrev) {
      runInAction(() => {
        const updated = Object.assign({}, uPrev, { seen_at: new Date().toISOString() })
        const next = this.urgentHistories.slice()
        next[uIdx] = updated
        this.urgentHistories = next
      })
    }

    try {
      await notifApi.markSeen(historyId)
    } catch (e) {
      if (wasUnread && prev) {
        runInAction(() => {
          const revert = Object.assign({}, prev, { seen_at: null })
          const next = this.histories.slice()
          const currentIdx = next.findIndex(h => h.id === historyId)
          if (currentIdx >= 0) {
            next[currentIdx] = revert
          }
          this.histories = next
          this.unreadCount = this.unreadCount + 1
        })
      }
      if (uWasUnread && uPrev) {
        runInAction(() => {
          const revert = Object.assign({}, uPrev, { seen_at: null })
          const next = this.urgentHistories.slice()
          const currentIdx = next.findIndex(h => h.id === historyId)
          if (currentIdx >= 0) {
            next[currentIdx] = revert
          }
          this.urgentHistories = next
        })
      }
    }
  }

  @action
  public async markAllAsRead() {
    const nowIso = new Date().toISOString()
    const hadUnread = this.unreadCount > 0
    if (!hadUnread) return
    runInAction(() => {
      this.histories = this.histories.map(h => h.seen_at ? h : Object.assign({}, h, { seen_at: nowIso }))
      this.urgentHistories = this.urgentHistories.map(h => h.seen_at ? h : Object.assign({}, h, { seen_at: nowIso }))
      this.unreadCount = 0
    })
    try {
      await notifApi.markAllRead()
    } catch (e) {
      await this.refreshUnreadCount()
      await Promise.all([this.fetchFeed(1), this.fetchUrgent()])
    }
  }

  // Unread urgent notifications delivered within the last 24h. Reads `nowTick`
  // (bumped each poll) so expired items drop off the banner without a manual refresh.
  @computed
  public get urgentItems(): NotificationHistory[] {
    const cutoff = this.nowTick - 24 * 60 * 60 * 1000
    return this.urgentHistories.filter(h => {
      if (h.seen_at) return false
      if (!h.delivered_at) return false
      const delivered = new Date(h.delivered_at).getTime()
      if (isNaN(delivered)) return false
      return delivered >= cutoff
    })
  }
}

export default NotificationStore
